<template>
  <AnalyticsWidget
    :title="`${taskType.name} DIFOT Breakdown`"
    subTitle="Orders completed during the date ranges"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight">

    <LoadingPanel :loading="loading" />
    <AnalyticsBreakdownTable v-if="!loading">
      <template v-slot:header>
        <tr>
          <th></th>
          <th>Completed</th>
          <th>Operational Score</th>
          <th>Commercial Score</th>
          <th>Hit</th>
          <th>Miss</th>
          <th>Exclude</th>
        </tr>
      </template>
      <template v-slot:body>
        <tr class="highlight">
          <td>
            Today
            <InlineHelpPopover title="Today" placement="right" inline>
              <p>Today&apos;s orders compared to previous working day&apos;s orders on {{formatDate(orderBreakdownData.day.previous.startDate)}}</p>              
            </InlineHelpPopover>
          </td>

          <AnalyticsBreakdownValueCell :value="taskTypeDayData('current').totalOrderCount" :previous="taskTypeDayData('previous').totalOrderCount" highlight>{{taskTypeDayData('current').totalOrderCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="taskTypeDayData('current').operationalScore" :previous="taskTypeDayData('previous').operationalScore" highlight>{{formatPercent(taskTypeDayData('current').operationalScore)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="taskTypeDayData('current').commercialScore" :previous="taskTypeDayData('previous').commercialScore" highlight>{{formatPercent(taskTypeDayData('current').commercialScore)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="taskTypeDayData('current').hitCount" :previous="taskTypeDayData('previous').hitCount" highlight>{{taskTypeDayData('current').hitCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="taskTypeDayData('current').missCount" :previous="taskTypeDayData('previous').missCount" highlight>{{taskTypeDayData('current').missCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="taskTypeDayData('current').excludeCount" :previous="taskTypeDayData('previous').excludeCount" highlight>{{taskTypeDayData('current').excludeCount}}</AnalyticsBreakdownValueCell>
        </tr>
        <tr>
          <td>
            This Week
            <InlineHelpPopover title="This Week" placement="right" inline>
              <p>This week&apos;s orders compared to same day of the previous week&apos;s orders from {{formatDate(orderBreakdownData.week.previous.startDate)}} to {{formatDate(orderBreakdownData.week.previous.endDate)}}</p>
            </InlineHelpPopover>
          </td>

          <AnalyticsBreakdownValueCell :value="taskTypeWeekData('current').totalOrderCount" :previous="taskTypeWeekData('previous').totalOrderCount" highlight>{{taskTypeWeekData('current').totalOrderCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="taskTypeWeekData('current').operationalScore" :previous="taskTypeWeekData('previous').operationalScore" highlight>{{formatPercent(taskTypeWeekData('current').operationalScore)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="taskTypeWeekData('current').commercialScore" :previous="taskTypeWeekData('previous').commercialScore" highlight>{{formatPercent(taskTypeWeekData('current').commercialScore)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="taskTypeWeekData('current').hitCount" :previous="taskTypeWeekData('previous').hitCount" highlight>{{taskTypeWeekData('current').hitCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="taskTypeWeekData('current').missCount" :previous="taskTypeWeekData('previous').missCount" highlight>{{taskTypeWeekData('current').missCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="taskTypeWeekData('current').excludeCount" :previous="taskTypeWeekData('previous').excludeCount" highlight>{{taskTypeWeekData('current').excludeCount}}</AnalyticsBreakdownValueCell>          
        </tr>
        <tr>
          <td>
            This Month
            <InlineHelpPopover title="This Month" placement="right" inline>
              <p>This month&apos;s orders compared to same day of the previous month&apos;s orders from {{formatDate(orderBreakdownData.month.previous.startDate)}} to {{formatDate(orderBreakdownData.month.previous.endDate)}}</p>
            </InlineHelpPopover>
          </td>

          <AnalyticsBreakdownValueCell :value="taskTypeMonthData('current').totalOrderCount" :previous="taskTypeMonthData('previous').totalOrderCount" highlight>{{taskTypeMonthData('current').totalOrderCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="taskTypeMonthData('current').operationalScore" :previous="taskTypeMonthData('previous').operationalScore" highlight>{{formatPercent(taskTypeMonthData('current').operationalScore)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="taskTypeMonthData('current').commercialScore" :previous="taskTypeMonthData('previous').commercialScore" highlight>{{formatPercent(taskTypeMonthData('current').commercialScore)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="taskTypeMonthData('current').hitCount" :previous="taskTypeMonthData('previous').hitCount" highlight>{{taskTypeMonthData('current').hitCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="taskTypeMonthData('current').missCount" :previous="taskTypeMonthData('previous').missCount" highlight>{{taskTypeMonthData('current').missCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="taskTypeMonthData('current').excludeCount" :previous="taskTypeMonthData('previous').excludeCount" highlight>{{taskTypeMonthData('current').excludeCount}}</AnalyticsBreakdownValueCell>          
        </tr>
        <tr>
          <td>
            This FY
            <InlineHelpPopover title="This Financial Year" placement="right" inline>
              <p>This financial year&apos;s orders compared to same day of the previous financial year&apos;s orders from {{formatDate(orderBreakdownData.financialYear.previous.startDate)}} to {{formatDate(orderBreakdownData.financialYear.previous.endDate)}}</p>
            </InlineHelpPopover>
          </td>

          <AnalyticsBreakdownValueCell :value="taskTypeFYData('current').totalOrderCount" :previous="taskTypeFYData('previous').totalOrderCount" highlight>{{taskTypeFYData('current').totalOrderCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="taskTypeFYData('current').operationalScore" :previous="taskTypeFYData('previous').operationalScore" highlight>{{formatPercent(taskTypeFYData('current').operationalScore)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="taskTypeFYData('current').commercialScore" :previous="taskTypeFYData('previous').commercialScore" highlight>{{formatPercent(taskTypeFYData('current').commercialScore)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="taskTypeFYData('current').hitCount" :previous="taskTypeFYData('previous').hitCount" highlight>{{taskTypeFYData('current').hitCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="taskTypeFYData('current').missCount" :previous="taskTypeFYData('previous').missCount" highlight>{{taskTypeFYData('current').missCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="taskTypeFYData('current').excludeCount" :previous="taskTypeFYData('previous').excludeCount" highlight>{{taskTypeFYData('current').excludeCount}}</AnalyticsBreakdownValueCell>          
        </tr>
      </template>

    </AnalyticsBreakdownTable>
  </AnalyticsWidget>
</template>


<script>
import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsBreakdownTable from '@/modules/intelligence/components/analyticsBreakdownTable'
import AnalyticsBreakdownValueCell from '@/modules/intelligence/components/analyticsBreakdownValueCell'

export default {
  name: 'OrderDIFOTBreakdownWidget',
  components: {
    AnalyticsWidget,
    AnalyticsBreakdownTable,
    AnalyticsBreakdownValueCell
  },
  props: {
    contactId: Number,
    taskType: Object,
    gridWidth: Number,
    gridHeight: Number
  },
  computed: {
    hasContactFilter() {
      return this.contactId != null
    },
    taskTypeList() {
      return this.orderBreakdownData.taskTypeList || []
    },
  },
  data() {
    return {
      loading: true,
      orderBreakdownData: {}
    }
  },
  watch: {
    contactId: function() {
      this.requestData()
    }
  },
  mounted() {
    this.loading = true
    this.requestData()

    this.subscribeEndpoint('/orders', () => {
      this.requestData()
    })
  },
  beforeDestroy() {
    this.unsubscribeEndpoint('/orders')
  },
  methods: {
    requestData() {
      this.$apiService.analyticsService.getOrderDIFOTBreakdown({ contactId: this.contactId })
      .then(response => {
        this.orderBreakdownData = response.data
      })
      .finally(() => {
        this.loading = false
      })
      .catch(err => {
        console.error(err)
      })
    },
    taskTypeDayData(dayType) {
      const taskTypeList = (this.orderBreakdownData.day[dayType].taskTypeData ? this.orderBreakdownData.day[dayType].taskTypeData : []).filter(taskType => taskType.taskTypeId === this.taskType.id)

      return taskTypeList.length > 0 ? taskTypeList[0] : { totalOrderCount: 0, operationalScore: 0, commercialScore: 0, hitCount: 0, missCount: 0, excludeCount: 0 }
    },
    taskTypeWeekData(weekType) {
      const taskTypeList = (this.orderBreakdownData.week[weekType].taskTypeData ? this.orderBreakdownData.week[weekType].taskTypeData : []).filter(taskType => taskType.taskTypeId === this.taskType.id)

      return taskTypeList.length > 0 ? taskTypeList[0] : { totalOrderCount: 0, operationalScore: 0, commercialScore: 0, hitCount: 0, missCount: 0, excludeCount: 0 }
    },
    taskTypeMonthData(monthType) {
      const taskTypeList = (this.orderBreakdownData.month[monthType].taskTypeData ? this.orderBreakdownData.month[monthType].taskTypeData : []).filter(taskType => taskType.taskTypeId === this.taskType.id)

      return taskTypeList.length > 0 ? taskTypeList[0] : { totalOrderCount: 0, operationalScore: 0, commercialScore: 0, hitCount: 0, missCount: 0, excludeCount: 0 }
    },
    taskTypeFYData(fyType) {
      const taskTypeList = (this.orderBreakdownData.financialYear[fyType].taskTypeData ? this.orderBreakdownData.financialYear[fyType].taskTypeData : []).filter(taskType => taskType.taskTypeId === this.taskType.id)

      return taskTypeList.length > 0 ? taskTypeList[0] : { totalOrderCount: 0, operationalScore: 0, commercialScore: 0, hitCount: 0, missCount: 0, excludeCount: 0 }
    }
    
  }
}
</script>